import type {ReactElement} from 'react';
import {useState} from 'react';
import {QRPayloadField} from '@Libraries/qr-code/qr-code.types';
import {openDesignWithQRHandoff} from '@Libraries/qr-code/qr-handoff.library';
import {getDuplicatePosterURL} from '@Libraries/poster-editor.library';
import {Text, TextSize} from '@Components/text';
import {QRField} from '@Components/qr-code-generator/components/qr-field/qr-field';
import {QRSuggestedTemplates} from '@Components/qr-code-generator/components/qr-suggested-templates/qr-suggested-templates';
import {useIsCompactQRTool} from '@Components/qr-code-generator/qr-code-generator-hooks';
import {UtmLinkPreview} from './components/utm-link-preview/utm-link-preview';
import {UtmLinkVideo} from './components/utm-link-video/utm-link-video';
import {useUtmFieldFilledTracking} from './utm-link-builder-hooks';
import {
  getFilledUtmParameterCount,
  getRequiredFieldLabel,
  getUtmHandoffItemObject,
  getUtmLinkResult,
  getUtmLinkState,
  isRequiredUtmParameter,
  isUtmLinkInputEmpty,
} from './utm-link-builder.library';
import {trackUtmCopy, trackUtmDesignSelected, trackUtmGalleryClick} from './utm-link-builder-tracking.library';
import type {UtmLinkBuilderProps, UtmLinkInput, UtmParameter, UtmParameterField} from './utm-link-builder.types';
import {EMPTY_UTM_LINK_INPUT, UTM_PARAMETER_FIELDS, UtmFormField} from './utm-link-builder.types';
import styles from './utm-link-builder.module.scss';

/**
 * The free UTM link builder: the page address and campaign parameters, the tagged link rebuilt on every keystroke, a looping demo and
 * QR designs to put the link on. Built from the QR tool's fields and design row so the two tools look alike.
 */
export function UtmLinkBuilder({videoSources, templateTiles}: UtmLinkBuilderProps): ReactElement {
  const [input, setInput] = useState<UtmLinkInput>(EMPTY_UTM_LINK_INPUT);
  const [hasHandoffFailed, setHasHandoffFailed] = useState(false);
  const isCompact = useIsCompactQRTool();
  const {onFieldFocus, onFieldBlur} = useUtmFieldFilledTracking();
  const result = getUtmLinkResult(input);

  /** With the link finished, the design opens with it in its QR code; before that, as the designer left it. Says whether a tab opened. */
  const openTemplate = async (designUrl: string): Promise<boolean> => {
    if (result.completeLink === null) {
      return window.open(designUrl, '_blank') !== null;
    }
    const wasHandedOff = await openDesignWithQRHandoff(designUrl, getUtmHandoffItemObject(result.completeLink));
    setHasHandoffFailed(!wasHandedOff);
    return wasHandedOff;
  };

  const getTemplatePosition = (designHashedId: string): number => {
    return (
      templateTiles.findIndex((tile) => {
        return tile.designHashedId === designHashedId;
      }) + 1
    );
  };

  const onTemplateSelect = async (designHashedId: string): Promise<void> => {
    setHasHandoffFailed(false);
    const wasOpened = await openTemplate(getDuplicatePosterURL(designHashedId));
    if (!wasOpened) {
      return;
    }
    trackUtmDesignSelected({linkState: getUtmLinkState(result), designHashedId, position: getTemplatePosition(designHashedId)});
  };

  const onWebsiteUrlChange = (websiteUrl: string): void => {
    setInput((currentInput): UtmLinkInput => {
      return {...currentInput, websiteUrl};
    });
  };

  const onParameterChange = (parameter: UtmParameter, value: string): void => {
    setInput((currentInput): UtmLinkInput => {
      return {...currentInput, parameterValues: {...currentInput.parameterValues, [parameter]: value}};
    });
  };

  const getParameterLabel = (parameterField: UtmParameterField): string => {
    const label = window.i18next.t(parameterField.labelKey);
    return isRequiredUtmParameter(parameterField.parameter) ? getRequiredFieldLabel(label) : label;
  };

  const renderParameterField = (parameterField: UtmParameterField): ReactElement => {
    return (
      <QRField
        key={parameterField.parameter}
        label={getParameterLabel(parameterField)}
        placeholder={window.i18next.t(parameterField.placeholderKey)}
        helpText={window.i18next.t(parameterField.helpKey)}
        value={input.parameterValues[parameterField.parameter]}
        onValueChange={(value): void => {
          onParameterChange(parameterField.parameter, value);
        }}
        onInputFocus={(value): void => {
          onFieldFocus(parameterField.parameter, value);
        }}
        onInputBlur={(value): void => {
          onFieldBlur(parameterField.parameter, value);
        }}
      />
    );
  };

  /** Left out on a phone, where it would only push the form down and spend the visitor's data on decoration. */
  const renderVideo = (): ReactElement | null => {
    if (videoSources === null || isCompact) {
      return null;
    }
    return <UtmLinkVideo sources={videoSources} />;
  };

  const renderHandoffError = (): ReactElement | null => {
    if (!hasHandoffFailed) {
      return null;
    }
    return <Text val={window.i18next.t('pmwjs_utm_builder_handoff_failed')} size={TextSize.SM} className={styles.utmHandoffError} attributes={{role: 'alert'}} />;
  };

  return (
    <div className={styles.utmToolCard}>
      <div className={styles.utmToolBody}>
        <div className={styles.utmToolMain}>
          <div className={styles.utmFormStack}>
            <QRField
              label={getRequiredFieldLabel(window.i18next.t('pmwjs_utm_builder_website_url_label'))}
              placeholder={window.i18next.t('pmwjs_utm_builder_website_url_placeholder')}
              helpText={window.i18next.t('pmwjs_utm_builder_website_url_help')}
              value={input.websiteUrl}
              field={QRPayloadField.LINK}
              issues={result.websiteUrlIssues}
              onValueChange={onWebsiteUrlChange}
              onInputFocus={(value): void => {
                onFieldFocus(UtmFormField.WEBSITE_URL, value);
              }}
              onInputBlur={(value): void => {
                onFieldBlur(UtmFormField.WEBSITE_URL, value);
              }}
            />
            <div className={styles.utmParameterGrid}>{UTM_PARAMETER_FIELDS.map(renderParameterField)}</div>
          </div>

          <UtmLinkPreview
            previewLink={result.previewLink}
            completeLink={result.completeLink}
            canClear={!isUtmLinkInputEmpty(input)}
            onClear={(): void => {
              setInput(EMPTY_UTM_LINK_INPUT);
            }}
            onCopyResult={(copyResult): void => {
              trackUtmCopy(copyResult, getFilledUtmParameterCount(input.parameterValues));
            }}
          />
        </div>

        {renderVideo()}
      </div>

      <QRSuggestedTemplates
        tiles={templateTiles}
        heading={window.i18next.t('pmwjs_utm_builder_suggested_templates_heading')}
        gridClasses={styles.utmSuggestedTemplatesGrid}
        onTemplateSelect={onTemplateSelect}
        onBrowseAllClick={(): void => {
          trackUtmGalleryClick(getUtmLinkState(result));
        }}
      />
      {renderHandoffError()}
    </div>
  );
}
